import React from 'react';
import { Link } from 'react-router-dom';
import { motion } from 'framer-motion';
import { 
  Battery, Power, Hand, Footprints, Eye, Radio, Shield, MapPin,
  Users, GraduationCap, Stethoscope, Building2, AlertTriangle,
  CheckCircle, ArrowRight, Zap, Box, CircleDot
} from 'lucide-react';

const TrainingPage = () => {
  const quickStartSteps = [
    {
      step: 1,
      title: 'Install the Battery',
      icon: <Battery className="w-8 h-8" />,
      instructions: [
        'Open the battery compartment',
        'Insert a 9V battery',
        'Ensure terminals are correctly aligned',
        'Close the compartment securely'
      ]
    },
    {
      step: 2,
      title: 'Power On',
      icon: <Power className="w-8 h-8" />,
      instructions: [
        'Locate the power switch at the top',
        'Toggle the switch to turn on',
        'Wait for startup confirmation'
      ]
    },
    {
      step: 3,
      title: 'Hold the Device',
      icon: <Hand className="w-8 h-8" />,
      instructions: [
        'Hold like a traditional mobility cane',
        'Ensure sensor area faces forward',
        'Grip comfortably at the handle'
      ]
    },
    {
      step: 4,
      title: 'Begin Walking',
      icon: <Footprints className="w-8 h-8" />,
      instructions: [
        'Move at your normal walking pace',
        'Clearway detects obstacles ahead',
        'Alerts provided through feedback',
        'Continue standard mobility techniques'
      ]
    }
  ];

  const deviceComponents = [
    { name: 'Power Switch', description: 'Located at the top of the device. Turns the system on and off.', icon: <Power className="w-6 h-6" />, position: 'top' },
    { name: 'Sensor Module', description: 'Detects obstacles ahead of the user using ultrasonic sensors.', icon: <Radio className="w-6 h-6" />, position: 'upper' },
    { name: 'Electronics Housing', description: 'Contains the microcontroller and power management system.', icon: <Zap className="w-6 h-6" />, position: 'middle' },
    { name: 'Power Source', description: 'Uses a standard 9V battery. Compartment located in housing.', icon: <Battery className="w-6 h-6" />, position: 'lower' },
  ];

  const safetyGuidelines = [
    {
      icon: <Shield className="w-6 h-6" />,
      title: 'Use With Traditional Cane',
      description: 'Clearway assists with obstacle awareness and should be used alongside established mobility techniques.'
    },
    {
      icon: <MapPin className="w-6 h-6" />,
      title: 'Start in Familiar Places',
      description: 'Begin using the device in safe, familiar environments before navigating complex areas.'
    },
    {
      icon: <Eye className="w-6 h-6" />,
      title: 'Stay Aware',
      description: 'Clearway assists detection but does not replace careful navigation and attention.'
    }
  ];

  const audienceCards = [
    {
      icon: <Users className="w-8 h-8" />,
      title: 'Users',
      description: 'Learning to interpret device feedback and integrate Clearway into everyday navigation.',
      color: 'blue'
    },
    {
      icon: <GraduationCap className="w-8 h-8" />,
      title: 'Schools',
      description: 'Supporting students using assistive technology and improving campus accessibility.',
      color: 'purple'
    },
    {
      icon: <Stethoscope className="w-8 h-8" />,
      title: 'Healthcare Providers',
      description: 'Helping users integrate Clearway into mobility training programs.',
      color: 'emerald'
    },
    {
      icon: <Building2 className="w-8 h-8" />,
      title: 'Accessibility Organizations',
      description: 'Running pilot programs and providing feedback to improve the device.',
      color: 'orange'
    }
  ];

  const maintenanceTips = [
    { icon: <Battery className="w-5 h-5" />, title: 'Battery Replacement', description: 'Replace the 9V battery when power becomes weak.' },
    { icon: <Radio className="w-5 h-5" />, title: 'Keep Sensors Clear', description: 'Ensure the sensor area is not blocked or obstructed.' },
    { icon: <Eye className="w-5 h-5" />, title: 'Inspect Before Use', description: 'Check the device for visible damage before each use.' },
  ];

  const colorClasses = {
    blue: 'bg-blue-500/10 border-blue-500/30 text-blue-400',
    purple: 'bg-purple-500/10 border-purple-500/30 text-purple-400',
    emerald: 'bg-emerald-500/10 border-emerald-500/30 text-emerald-400',
    orange: 'bg-orange-500/10 border-orange-500/30 text-orange-400',
  };

  return (
    <div className="pt-24">
      {/* Hero Section */}
      <section className="py-16 px-6">
        <div className="max-w-7xl mx-auto">
          <div className="grid lg:grid-cols-2 gap-12 items-center">
            <motion.div
              initial={{ opacity: 0, x: -40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6 }}
            >
              <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full bg-blue-500/10 border border-blue-500/20 mb-6">
                <GraduationCap className="w-4 h-4 text-blue-400" />
                <span className="text-blue-400 text-sm font-medium">Setup Guide</span>
              </div>
              <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold tracking-tight mb-6">
                ClearWay
                <br />
                <span className="text-gray-500">Training & Setup</span>
              </h1>
              <p className="text-xl text-gray-400 mb-8">
                Learn how to safely set up and use ClearWay. 
                Visual guides for users, caregivers, and organizations.
              </p>
              <div className="flex flex-wrap gap-4">
                <a href="#quick-start" className="inline-flex items-center gap-2 px-6 py-3 rounded-full bg-blue-500 hover:bg-blue-600 text-white font-medium transition-colors">
                  Quick Start Guide
                  <ArrowRight className="w-4 h-4" />
                </a>
                <Link to="/safety-notice" className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-gray-700 hover:bg-white/5 text-white font-medium transition-colors">
                  Safety Notice
                </Link>
              </div>
            </motion.div>

            {/* Device Diagram */}
            <motion.div
              initial={{ opacity: 0, x: 40 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ duration: 0.6, delay: 0.2 }}
              className="relative"
            >
              <div className="p-8 rounded-3xl bg-gradient-to-br from-gray-800/30 to-gray-900/50 border border-gray-800/50">
                {/* Simplified stick diagram */}
                <div className="relative mx-auto w-32">
                  {/* Stick body */}
                  <div className="relative">
                    {/* Handle */}
                    <div className="w-16 h-8 mx-auto rounded-t-full bg-gradient-to-b from-gray-600 to-gray-700 border-2 border-gray-500" />
                    
                    {/* Power switch area */}
                    <div className="w-12 h-6 mx-auto bg-gray-700 border-x-2 border-gray-500 flex items-center justify-center">
                      <div className="w-4 h-2 rounded-full bg-green-500" />
                    </div>
                    
                    {/* Electronics housing */}
                    <div className="w-14 h-20 mx-auto bg-gradient-to-b from-blue-900 to-blue-950 border-2 border-blue-800 rounded-lg flex items-center justify-center">
                      <Radio className="w-6 h-6 text-blue-400" />
                    </div>
                    
                    {/* Shaft */}
                    <div className="w-4 h-48 mx-auto bg-gradient-to-b from-gray-600 to-gray-700 border-x border-gray-500" />
                    
                    {/* Tip */}
                    <div className="w-6 h-4 mx-auto rounded-b-full bg-gray-500" />
                  </div>
                </div>

                {/* Labels */}
                <div className="mt-8 space-y-3">
                  {deviceComponents.map((comp, i) => (
                    <div key={comp.name} className="flex items-center gap-3 text-sm">
                      <div className="w-8 h-8 rounded-lg bg-blue-500/10 flex items-center justify-center text-blue-400">
                        {comp.icon}
                      </div>
                      <div>
                        <p className="font-medium text-white">{comp.name}</p>
                        <p className="text-xs text-gray-500">{comp.description}</p>
                      </div>
                    </div>
                  ))}
                </div>
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Quick Start Guide */}
      <section id="quick-start" className="py-20 px-6 bg-[#0f0f0f]">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-16"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Quick Start Guide</h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              Get started with ClearWay in four simple steps.
            </p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {quickStartSteps.map((item, index) => (
              <motion.div
                key={item.step}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="relative p-6 rounded-2xl bg-gradient-to-b from-gray-800/50 to-transparent border border-gray-800/50"
              >
                {/* Step number */}
                <div className="absolute -top-4 -left-2 w-10 h-10 rounded-full bg-blue-500 flex items-center justify-center text-white font-bold text-lg">
                  {item.step}
                </div>
                
                {/* Icon */}
                <div className="w-16 h-16 rounded-2xl bg-blue-500/10 flex items-center justify-center text-blue-400 mb-4 mt-2">
                  {item.icon}
                </div>
                
                <h3 className="text-lg font-semibold mb-3">{item.title}</h3>
                
                <ul className="space-y-2">
                  {item.instructions.map((inst, i) => (
                    <li key={i} className="flex items-start gap-2 text-sm text-gray-400">
                      <CheckCircle className="w-4 h-4 text-green-400 flex-shrink-0 mt-0.5" />
                      <span>{inst}</span>
                    </li>
                  ))}
                </ul>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Obstacle Detection Explanation */}
      <section className="py-20 px-6">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Understanding Obstacle Detection</h2>
            <p className="text-gray-400 max-w-xl mx-auto">
              How ClearWay helps you navigate safely.
            </p>
          </motion.div>

          {/* Visual flow diagram */}
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-3xl bg-gradient-to-br from-blue-500/5 to-purple-500/5 border border-gray-800/50"
          >
            <div className="flex flex-col md:flex-row items-center justify-between gap-6">
              {/* User */}
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-blue-500/10 border-2 border-blue-500/30 flex items-center justify-center mx-auto mb-3">
                  <Users className="w-10 h-10 text-blue-400" />
                </div>
                <p className="font-medium">User</p>
                <p className="text-xs text-gray-500">Holds device</p>
              </div>

              {/* Arrow */}
              <ArrowRight className="w-8 h-8 text-gray-600 hidden md:block" />
              <div className="w-8 h-8 text-gray-600 md:hidden rotate-90">
                <ArrowRight className="w-8 h-8" />
              </div>

              {/* Sensor */}
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-purple-500/10 border-2 border-purple-500/30 flex items-center justify-center mx-auto mb-3">
                  <Radio className="w-10 h-10 text-purple-400" />
                </div>
                <p className="font-medium">Sensor</p>
                <p className="text-xs text-gray-500">Scans ahead</p>
              </div>

              {/* Arrow */}
              <ArrowRight className="w-8 h-8 text-gray-600 hidden md:block" />
              <div className="w-8 h-8 text-gray-600 md:hidden rotate-90">
                <ArrowRight className="w-8 h-8" />
              </div>

              {/* Obstacle */}
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-orange-500/10 border-2 border-orange-500/30 flex items-center justify-center mx-auto mb-3">
                  <Box className="w-10 h-10 text-orange-400" />
                </div>
                <p className="font-medium">Obstacle</p>
                <p className="text-xs text-gray-500">Detected</p>
              </div>

              {/* Arrow */}
              <ArrowRight className="w-8 h-8 text-gray-600 hidden md:block" />
              <div className="w-8 h-8 text-gray-600 md:hidden rotate-90">
                <ArrowRight className="w-8 h-8" />
              </div>

              {/* Alert */}
              <div className="text-center">
                <div className="w-20 h-20 rounded-full bg-green-500/10 border-2 border-green-500/30 flex items-center justify-center mx-auto mb-3">
                  <CircleDot className="w-10 h-10 text-green-400" />
                </div>
                <p className="font-medium">Alert</p>
                <p className="text-xs text-gray-500">User notified</p>
              </div>
            </div>

            <div className="mt-8 grid md:grid-cols-3 gap-4 text-center">
              <div className="p-4 rounded-xl bg-gray-800/30">
                <p className="text-sm text-gray-400">ClearWay uses sensors to detect obstacles ahead</p>
              </div>
              <div className="p-4 rounded-xl bg-gray-800/30">
                <p className="text-sm text-gray-400">The device provides feedback when an obstacle is detected</p>
              </div>
              <div className="p-4 rounded-xl bg-gray-800/30">
                <p className="text-sm text-gray-400">Detection works best for objects within the sensor&apos;s range</p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Safety Guidelines */}
      <section className="py-20 px-6 bg-[#0f0f0f]">
        <div className="max-w-5xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Safe Usage Guidelines</h2>
            <p className="text-gray-400">Important practices for using ClearWay safely.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {safetyGuidelines.map((item, index) => (
              <motion.div
                key={item.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-gradient-to-b from-yellow-500/5 to-transparent border border-yellow-500/20"
              >
                <div className="w-14 h-14 rounded-2xl bg-yellow-500/10 flex items-center justify-center text-yellow-400 mb-4">
                  {item.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2">{item.title}</h3>
                <p className="text-gray-400 text-sm">{item.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Training for Different Groups */}
      <section className="py-20 px-6">
        <div className="max-w-6xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Training for Different Groups</h2>
            <p className="text-gray-400">Resources tailored to your needs.</p>
          </motion.div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {audienceCards.map((card, index) => (
              <motion.div
                key={card.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className={`p-6 rounded-2xl border ${colorClasses[card.color]}`}
              >
                <div className={`w-16 h-16 rounded-2xl ${colorClasses[card.color].split(' ')[0]} flex items-center justify-center mb-4`}>
                  {card.icon}
                </div>
                <h3 className="text-lg font-semibold mb-2 text-white">{card.title}</h3>
                <p className="text-gray-400 text-sm">{card.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Testing & Pilot Programs */}
      <section className="py-20 px-6 bg-[#0f0f0f]">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-3xl bg-gradient-to-br from-blue-500/5 to-purple-500/5 border border-gray-800/50"
          >
            <div className="flex flex-col md:flex-row items-center gap-8">
              {/* Map icons */}
              <div className="flex gap-4">
                <div className="w-20 h-20 rounded-2xl bg-orange-500/10 border border-orange-500/30 flex flex-col items-center justify-center">
                  <MapPin className="w-8 h-8 text-orange-400" />
                  <span className="text-xs text-orange-400 mt-1">India</span>
                </div>
                <div className="w-20 h-20 rounded-2xl bg-emerald-500/10 border border-emerald-500/30 flex flex-col items-center justify-center">
                  <MapPin className="w-8 h-8 text-emerald-400" />
                  <span className="text-xs text-emerald-400 mt-1">UAE</span>
                </div>
              </div>
              
              <div className="flex-1 text-center md:text-left">
                <h3 className="text-2xl font-bold mb-3">Testing & Pilot Programs</h3>
                <p className="text-gray-400">
                  ClearWay is currently preparing for early testing with users, including students 
                  in Dubai and individuals in India. These trials help refine the device and 
                  improve real-world performance.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Maintenance */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="text-center mb-12"
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-4">Maintenance</h2>
            <p className="text-gray-400">Keep your ClearWay in optimal condition.</p>
          </motion.div>

          <div className="grid md:grid-cols-3 gap-6">
            {maintenanceTips.map((tip, index) => (
              <motion.div
                key={tip.title}
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
                className="p-6 rounded-2xl bg-gray-800/30 border border-gray-800/50"
              >
                <div className="w-12 h-12 rounded-xl bg-blue-500/10 flex items-center justify-center text-blue-400 mb-4">
                  {tip.icon}
                </div>
                <h3 className="font-semibold mb-2">{tip.title}</h3>
                <p className="text-gray-400 text-sm">{tip.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* Safety Reminder */}
      <section className="py-16 px-6 bg-[#0f0f0f]">
        <div className="max-w-3xl mx-auto">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
            className="p-8 rounded-2xl bg-yellow-500/5 border-2 border-yellow-500/30 text-center"
          >
            <AlertTriangle className="w-12 h-12 text-yellow-400 mx-auto mb-4" />
            <h3 className="text-xl font-bold mb-3 text-yellow-400">Safety Reminder</h3>
            <p className="text-gray-300 mb-6">
              ClearWay is designed to assist with obstacle awareness but should always be used 
              alongside traditional mobility practices.
            </p>
            <Link 
              to="/safety-notice"
              className="inline-flex items-center gap-2 px-6 py-3 rounded-full border border-yellow-500/50 text-yellow-400 hover:bg-yellow-500/10 transition-colors"
            >
              Read Full Safety Notice
              <ArrowRight className="w-4 h-4" />
            </Link>
          </motion.div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6">
        <div className="max-w-4xl mx-auto text-center">
          <motion.div
            initial={{ opacity: 0, y: 30 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <h2 className="text-3xl md:text-4xl font-bold mb-6">Ready to Get Started?</h2>
            <p className="text-gray-400 mb-8 max-w-xl mx-auto">
              Have questions about setup or training? We&apos;re here to help.
            </p>
            <div className="flex flex-col sm:flex-row items-center justify-center gap-4">
              <Link
                to="/shop"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full bg-white text-black font-medium hover:bg-gray-100 transition-all"
              >
                Get ClearWay
                <ArrowRight className="w-4 h-4" />
              </Link>
              <Link
                to="/contact"
                className="inline-flex items-center gap-2 px-8 py-4 rounded-full border border-white/20 text-white font-medium hover:bg-white/10 transition-all"
              >
                Contact Support
              </Link>
            </div>
          </motion.div>
        </div>
      </section>
    </div>
  );
};

export default TrainingPage;
